import React, { useState } from "react";
import { createPortal } from "react-dom";
import { API } from "./api";
import { Share2, Link2, Mail, Check, MessageCircle, X, Facebook, Linkedin, Twitter, Loader2, Sparkles } from "lucide-react";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" /></svg>
);

export function ShareButtons({ slug, title = "", price = "", city = "", variant = "dark", compact = false, minimal = false, className = "" }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [tone, setTone] = useState("Professionnel");
  const [genText, setGenText] = useState("");
  const [busyNet, setBusyNet] = useState("");
  const [note, setNote] = useState("");
  const PROD = "https://off-markette.com"; // les partages pointent toujours vers la production
  const url = `${PROD}/bien/${slug}`;
  const shareUrl = `${PROD}/api/listings/${slug}/share`; // page Open Graph (photo + titre + code) pour l'aperçu des réseaux
  const friendly = `Regarde ce bien off market qui pourrait te plaire : ${title}${city ? ` à ${city}` : ""}${price ? ` — ${price}` : ""}. À découvrir sur OFF MARKETTE 👇`;
  const enc = encodeURIComponent;
  const TONES = ["Prestige", "Professionnel", "Familial", "Investisseur", "Luxe", "Émotionnel", "Dynamique", "Local"];

  const generateFor = async (network, useTone) => {
    try {
      const r = await fetch(`${API}/listings/${slug}/share-text`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ network, tone: useTone || tone }),
      });
      const d = await r.json();
      return (d.text || "").trim() || friendly;
    } catch (_) { return friendly; }
  };

  const buildLink = (network, msg) => ({
    whatsapp: `https://wa.me/?text=${enc(msg + "\n" + shareUrl)}`,
    email: `mailto:?subject=${enc(`${title || "Un bien OFF MARKETTE"} — à te partager`)}&body=${enc(msg + "\n\n" + url)}`,
    messenger: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(msg)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(msg)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?text=${enc(msg)}&url=${enc(shareUrl)}`,
  }[network]);

  // Un clic : partage le texte prévisualisé (ou le génère au besoin), le copie, puis ouvre le partage.
  const shareOn = async (network) => {
    if (busyNet) return;
    const external = network !== "email";
    const win = external ? window.open("", "_blank") : null; // ouvert AVANT l'async pour éviter le blocage popup
    setBusyNet(network); setNote("");
    const text = (genText || "").trim() || await generateFor(network);
    setGenText(text);
    setBusyNet("");
    try { await navigator.clipboard.writeText(`${text}\n${url}`); } catch (_) {}
    const link = buildLink(network, text);
    if (network === "email") { window.location.href = link; }
    else if (win) { win.location.href = link; } else { window.open(link, "_blank"); }
    track(network);
    setNote(network === "linkedin" || network === "facebook" || network === "messenger"
      ? "✅ Texte copié — collez-le (Ctrl/Cmd+V) dans votre publication si besoin."
      : "✅ Texte pré-rempli dans le partage.");
  };

  const previewFor = async (useTone) => { setBusyNet("preview"); setGenText(await generateFor("facebook", useTone)); setBusyNet(""); };
  const copy = async (e) => {
    e && e.preventDefault(); e && e.stopPropagation();
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
    track("link");
  };
  const track = (network) => {
    try {
      fetch(`${API}/listings/${slug}/share-track`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ network }),
      }).catch(() => {});
    } catch (_) { /* noop */ }
  };
  const stop = (e) => e.stopPropagation();
  const openModal = (e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); };

  const Modal = () => (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid={`share-modal-${slug}`}>
      <div className="relative w-full max-w-xl rounded-[5px] border border-[#C19A6B]/30 bg-[#0d0d10] shadow-2xl p-6 sm:p-7 text-white" onClick={stop}>
        <div aria-hidden="true" className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#C19A6B]/20 blur-3xl pointer-events-none" />
        <button onClick={() => setOpen(false)} aria-label="Fermer" data-testid={`share-close-${slug}`} className="absolute top-3 right-3 text-zinc-400 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-3">
          <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-[5px] bg-[#C19A6B]/15 border border-[#C19A6B]/40 text-[#C19A6B]"><Share2 className="w-5 h-5" /></span>
          <div className="min-w-0">
            <p className="calli text-2xl text-[#C19A6B] leading-none">Partager ce bien sur vos réseaux</p>
            <p className="text-sm text-zinc-400 mt-1">Diffusez cette opportunité en un clic sur vos réseaux.</p>
          </div>
        </div>
        {(title || price) && (
          <p className="text-sm text-zinc-200 mt-4 pt-4 border-t border-white/10 truncate">
            <span className="text-white">{title}</span>{city ? ` · ${city}` : ""}{price ? <span className="text-[#C19A6B] font-semibold"> · {price}</span> : ""}
          </p>
        )}
        <div className="mt-4" data-testid="share-tone-block">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">1 · Choisissez le style — le texte se génère automatiquement</p>
          <div className="flex flex-wrap gap-1.5">
            {TONES.map((t) => (
              <button key={t} onClick={() => { setTone(t); setNote(""); previewFor(t); }} data-testid={`share-tone-${t}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${tone === t ? "bg-[#C19A6B] border-[#C19A6B] text-white" : "border-white/20 text-zinc-300 hover:border-[#C19A6B]/60 hover:text-[#C19A6B]"}`}>{t}</button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 flex items-center justify-between">
            <span>2 · Aperçu du message (modifiable)</span>
            <button onClick={() => previewFor(tone)} disabled={!!busyNet} className="inline-flex items-center gap-1 text-[#C19A6B] hover:text-[#d1af84] normal-case tracking-normal disabled:opacity-50" data-testid="share-regenerate">
              {busyNet === "preview" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Régénérer
            </button>
          </p>
          <textarea value={busyNet === "preview" ? "Génération du message…" : genText} onChange={(e) => setGenText(e.target.value)} readOnly={busyNet === "preview"}
            data-testid="share-generated-text" placeholder="Choisissez un style ci-dessus pour générer un message optimisé (modifiable), ou partagez directement."
            className="w-full h-24 bg-white/[0.04] border border-white/12 rounded-[5px] px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B] resize-none" />
        </div>

        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">3 · Partagez sur le réseau de votre choix</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: "facebook", label: "Facebook", Icon: Facebook },
              { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
              { key: "twitter", label: "X (Twitter)", Icon: Twitter },
              { key: "whatsapp", label: "WhatsApp", Icon: WhatsAppIcon },
              { key: "email", label: "E-mail", Icon: Mail },
              { key: "messenger", label: "Messenger", Icon: MessageCircle },
            ].map(({ key, label, Icon }) => (
              <button key={key} onClick={() => shareOn(key)} disabled={!!busyNet} data-testid={`share-${key}-${slug}`}
                className="relative flex flex-col items-center justify-center gap-1.5 py-3.5 px-2 rounded-[5px] border border-white/12 bg-white/[0.04] hover:bg-[#C19A6B]/15 hover:border-[#C19A6B]/50 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                {busyNet === key ? <Loader2 className="w-5 h-5 text-[#C19A6B] animate-spin" /> : <Icon className="w-5 h-5 text-[#C19A6B]" />}
                <span className="text-[10px] uppercase tracking-wide text-zinc-300 text-center leading-tight">Partager sur<br />{label}</span>
              </button>
            ))}
            <button onClick={copy} data-testid={`share-copy-${slug}`} className="flex flex-col items-center justify-center gap-1.5 py-3.5 px-2 rounded-[5px] border border-white/12 bg-white/[0.04] hover:bg-[#C19A6B]/15 hover:border-[#C19A6B]/50 hover:-translate-y-0.5 transition-all">
              {copied ? <Check className="w-5 h-5 text-green-400" /> : <Link2 className="w-5 h-5 text-[#C19A6B]" />}
              <span className="text-[10px] uppercase tracking-wide text-zinc-300 text-center leading-tight">{copied ? "Copié !" : "Copier le lien"}</span>
            </button>
          </div>
          {note && <p className="text-[11px] text-[#C19A6B] mt-2" data-testid="share-note">{note}</p>}
        </div>
      </div>
    </div>
  );

  if (minimal) {
    return (
      <>
        <button type="button" onClick={openModal} aria-label="Partager cette annonce" data-testid={`share-native-${slug}`}
          className={`share-glow inline-flex items-center justify-center px-1.5 py-1 rounded-[5px] bg-[#C19A6B] text-white hover:opacity-90 transition-opacity ${className}`}>
          <Share2 className="w-3.5 h-3.5" />
        </button>
        {open && createPortal(<Modal />, document.body)}
      </>
    );
  }

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`} data-testid={`share-buttons-${slug}`} onClick={stop}>
        <button type="button" onClick={openModal} data-testid={`share-open-${slug}`} className={`share-glow inline-flex items-center justify-center gap-1.5 bg-[#C19A6B] text-white hover:opacity-90 rounded-[5px] transition-opacity ${compact ? "w-8 h-8" : "px-3 py-2 text-xs uppercase tracking-wider"}`}>
          <Share2 className="w-4 h-4" />{!compact && "Partager sur mon réseau"}
        </button>
      </div>
      {open && createPortal(<Modal />, document.body)}
    </>
  );
}
